
class InfiniteList extends HTMLElement {
  connectedCallback() {
    this.items = this.querySelector('[data-infinite-items]');
    this.nav = this.querySelector('[data-infinite-nav]');
    if (!this.items) return;

    this.auto = this.dataset.infinite === 'true';
    this.sectionId = this.dataset.sectionId;
    this.soldOutLast = this.dataset.soldOutLast === 'true' && !this.dataset.availFilter;

    this.tail = [];
    this.buffer = [];
    this.busy = false;
    this.step = this.items.children.length || 16;
    this.nextUrl = this.querySelector('[data-infinite-next]')?.href || null;

    this.bind();

    if (this.soldOutLast) {
      this.hold(this.items);
      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.topUp(), 150);
      };
      window.addEventListener('resize', this.onResize);
      this.topUp();
    }

    if (this.auto) this.watch();
  }

  disconnectedCallback() {
    this.io?.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }

  bind() {
    this.addEventListener('click', (e) => {
      const link = e.target.closest('[data-infinite-next]');
      if (!link) return;
      e.preventDefault();
      this.more();
    });
  }

  watch() {
    this.io?.disconnect();
    const next = this.querySelector('[data-infinite-next]');
    if (!next || this.nav?.hidden) return;
    this.io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) this.more();
    }, { rootMargin: '600px 0px' });
    this.io.observe(next);
  }

  hold(root) {
    Array.from(root.children).forEach((li) => {
      const card = li.querySelector('[data-product-card]');
      if (card && card.dataset.available === 'false') {
        li.remove();
        this.tail.push(li);
      }
    });
  }

  columns() {
    const cols = getComputedStyle(this.items).gridTemplateColumns.split(' ').filter(Boolean).length;
    return cols > 0 ? cols : 1;
  }

  gap() {
    const cols = this.columns();
    const rest = this.items.children.length % cols;
    return rest ? cols - rest : 0;
  }

  reveal(count) {
    let shown = 0;
    while (shown < count && this.buffer.length) {
      this.items.append(this.buffer.shift());
      shown += 1;
    }
    return shown;
  }

  async grow(count) {
    let shown = 0;
    let guard = 0;
    while (shown < count && guard < 40) {
      shown += this.reveal(count - shown);
      if (shown >= count) break;
      if (!this.nextUrl) break;
      guard += 1;
      if (!(await this.page(this.nextUrl))) break;
    }
    return shown;
  }

  async more() {
    if (this.busy) return;
    this.busy = true;
    try {
      const shown = await this.grow(this.step);
      if (this.soldOutLast) await this.grow(this.gap());
      this.settle();
      this.updateNav();
      this.syncShown();
      this.dispatchEvent(new CustomEvent('infinite:loaded', {
        bubbles: true,
        detail: { page: this.dataset.page, added: shown },
      }));
    } finally {
      this.busy = false;
      if (this.auto) this.watch();
    }
  }

  async topUp() {
    if (!this.soldOutLast || this.busy) return;
    this.busy = true;
    try {
      await this.grow(this.gap());
      this.settle();
      this.updateNav();
      this.syncShown();
    } finally {
      this.busy = false;
      if (this.auto) this.watch();
    }
  }

  settle() {
    if (!this.soldOutLast || !this.tail.length) return;
    if (this.nextUrl || this.buffer.length) return;
    this.items.append(...this.tail);
    const released = this.tail.length;
    this.tail = [];
    this.dispatchEvent(new CustomEvent('infinite:sold-out-released', {
      bubbles: true,
      detail: { released },
    }));
  }

  updateNav() {
    if (!this.nav) return;
    this.nav.hidden = !this.nextUrl && !this.buffer.length;
  }

  async page(href) {
    const button = this.querySelector('[data-infinite-next]');
    button?.classList.add('is-loading');
    this.io?.disconnect();

    try {
      const url = new URL(href, location.origin);
      url.searchParams.set('section_id', this.sectionId);
      const res = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-infinite-items]');
      if (!fresh) throw new Error('у відповіді немає списку товарів');

      const page = new URL(href, location.origin).searchParams.get('page') || '1';
      Array.from(fresh.children).forEach((li) => {
        li.dataset.sourcePage = page;
        const card = li.querySelector('[data-product-card]');
        if (this.soldOutLast && card && card.dataset.available === 'false') this.tail.push(li);
        else this.buffer.push(li);
      });

      this.nextUrl = doc.querySelector('[data-infinite-next]')?.href || null;
      return true;
    } catch (err) {
      console.warn('infinite-list:', err.message);
      this.nextUrl = null;
      return false;
    } finally {
      button?.classList.remove('is-loading');
    }
  }

  syncShown() {
    const page = String(Math.max(1, Math.ceil(this.items.children.length / this.step)));
    if (page !== this.dataset.page) this.syncPage(page);
  }

  syncPage(page) {
    const url = new URL(location.href);
    if (Number(page) > 1) url.searchParams.set('page', page);
    else url.searchParams.delete('page');
    history.replaceState(history.state, '', url);
    this.dataset.page = page;
  }
}

customElements.define('infinite-list', InfiniteList);
